<template>
  <component :is="tag" class="fts-tippy">
    <component 
      :is="triggerTag || tag" 
      class="tippy-trigger" 
      :class="[triggerClass, appendTriggerClass]" 
      @click="onClickTrigger" 
      ref="trigger"
    >
      <slot /> <!-- default slot -->
      <slot name="trigger" /> <!-- fallback with slot name -->
    </component>
    <component 
      :is="contentTag || tag" 
      class="tippy-content-wrapper" 
      :class="[contentClass, appendContentClass]" 
      ref="content"
    >
      <slot
        name="content"
        v-bind="tippyContent"
        :content="tippyContent"
        :loading="tippyContentLoading"
        v-if="isContentVisible"
      />
    </component>
  </component>
</template>

<script>
import tippy, {followCursor, roundArrow} from "tippy.js";
import { clientStore } from '../store/clientStore';
import 'tippy.js/dist/svg-arrow.css';
import 'tippy.js/themes/light.css';
import 'tippy.js/themes/light-border.css';
import 'tippy.js/animations/shift-away.css';

const defaultOptions = Object.freeze({
  appendTo: () => document.body,
  allowHTML: true,
  arrow: false,
  delay: [500, 100], /* ms [show, hide] */
  duration: [300, 100], /* ms [show, hide] */
  followCursor: false,
  interactive: false,
  maxWidth: 350,
  placement: "top",
  theme: "",
  trigger: "mouseenter focus",
  animation: 'shift-away'
});

export default {
  props: {
    tag: {
      type: String,
      default: 'div'
    },
    triggerTag: String,
    contentTag: String,
    triggerClass: String, /* deprecated */
    contentClass: String, /* deprecated */
    content: [String, Number, Boolean],
    options: Object,
    request: Object,
    /* append class */
    appendRootClass: String,
    appendBoxClass: String,
    appendTriggerClass: String, /* replace triggerClass */
    appendContentClass: String, /* replace contentClass */
    /* popper options */
    popperPreventOverflow: Boolean,
    /* event */
    stopPropagation: Boolean,
    preventDefault: Boolean
  },
  data: () => ({
    tippyInstance: null,
    tippyContent: null,
    tippyContentLoading: false,
    isContentVisible: false
  }),
  methods: {
    async init() {
      let options = {...defaultOptions};
      /* options */
      if (this.options) {
        for (const key of Object.keys(this.options)) {
          if (options.hasOwnProperty(key)) {
            options[key] = this.options[key];
          }
        }
      }
      /* arrow */
      if (options.arrow) {
        options.arrow = roundArrow;
      }
      /* content */
      if (this.content) {
        options.content = this.content
      } else {
        options.content = this.$refs.content;
      }
      /* popper options */
      let popperModifiers = [];
      if (this.popperPreventOverflow) {
        popperModifiers.push({
          name: 'preventOverflow',
          options: {
            altAxis: true,
            tether: false,
            padding: 6
          },
        });
      }
      if (popperModifiers.length) {
        options.popperOptions = {
          modifiers: popperModifiers,
        }
      }
      /* events */
      options.onTrigger = (instance, event) => {
        this.$emit('onTrigger', { instance: instance, event: event });
      };
      options.onShow = async tippy => {
        this.isContentVisible = true;
        if (this.request) {
          try {
            this.tippyContentLoading = true;
            const res = await clientStore.dispatch(
              this.request.dispatchName,
              this.request.payload
            );
            this.tippyContent = res.data;
          } catch(err) {
            console.error(err);
          } finally {
            this.tippyContentLoading = false;
            tippy.popperInstance.update();
          }
        } else if (this.content && tippy.props.content !== this.content) {
          tippy.setContent(this.content);
        }
      }
      options.onHidden = () => {
        this.isContentVisible = false;
      }
      /* create tippy */
      this.tippyInstance = tippy(this.$refs.trigger, {
        ...options,
        plugins: [followCursor],
      });
      /* append root class (experiment) */
      if (this.appendRootClass) {
        try {
          this.tippyInstance.popper.classList.add(this.appendRootClass);
        } catch(err) {
          console.warn(`[DOMException] 'appendContentClass'`, err);
        }
      }
      /* append box class */
      if (this.appendBoxClass) {
        try {
          this.tippyInstance.popper.firstChild.classList.add(this.appendBoxClass);
        } catch(err) {
          console.warn(`[DOMException] 'appendContentClass'`, err);
        }
      }
      /* popper prevent overflow */
      if (this.popperPreventOverflow) {
        try {
          Object.assign(this.tippyInstance.popper.style, {
            "max-height": "calc(100% - 6px)",
            "max-width": "calc(100% - 6px)",
            "display": "flex",
            "flex-direction": "column"
          });
          this.tippyInstance.popper.firstChild.classList.add('tippy-box--prevent-overflow');
        } catch(err) {
          console.warn(`[DOMException] 'popperPreventOverflow'`, err);
        }
      }
    },
    onClickTrigger(event) {
      if (this.stopPropagation)
        event.stopPropagation();

      if (this.preventDefault)
        event.preventDefault();

      this.$emit("trigger:click");
    }
  },
  mounted() {
    this.init();
  },
  beforeDestroy() {
    if (this.tippyInstance) {
      this.tippyInstance.destroy();
      this.tippyInstance = null;
    }
  }
};
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-tippy {
  .tippy-trigger {
    cursor: default;
  }
}

.tippy-box {
  max-height: 100%;
  max-width: 100%;
  padding: 0.5rem 0.5rem;
  font-size: 0.75rem;
  background-color: var(--fts-popover-background);
  text-align: inherit;
  color: var(--fts-light);
  border-radius: 0.25rem;
  box-shadow: var(--fts-elevation-3);
  .tippy-arrow {
    fill: var(--fts-popover-background);
  }
  /* tomato */
  &[data-theme~='tomato'] {
    background-color: tomato;
    color: yellow;
    &[data-placement^='top'] > .tippy-svg-arrow {
      fill: tomato;
    }
    &[data-placement^='bottom'] > .tippy-svg-arrow {
      fill: tomato;
    }
    &[data-placement^='left'] > .tippy-svg-arrow {
      fill: tomato;
    }
    &[data-placement^='right'] > .tippy-svg-arrow {
      fill: tomato;
    }
  }
  /* light */
  &[data-theme~='light-border'] {
    border: none;
    box-shadow: var(--fts-elevation-3);
    background-color: var(--fts-popover-alt-background);
    color: var(--fts-text);
    >.tippy-svg-arrow {
      fill: var(--fts-popover-alt-background);
    }
  }
  /* tippy prevent overflow */
  @mixin tippy-prevent-overflow {
    display: flex;
    flex-direction: column;
    overflow: auto;
    flex: 1;
  }
  &.tippy-box--prevent-overflow {
    @include tippy-prevent-overflow;
    .tippy-content {
    @include tippy-prevent-overflow;
    }
    .tippy-content-wrapper {
      @include tippy-prevent-overflow;
    }
  }
}
.tippy-hint {
  color: var(--fts-default);
  text-align: center;
  .tippy-hint-highlight {
    font-weight: 500;
    color: var(--fts-light);
  }
}
</style>